import React, { useEffect, useState, useMemo } from 'react';
import {
  UserPlus,
  Shield,
  CheckCircle,
  Mail,
  LogIn,
  LogOut,
  RefreshCcw,
  Users as UsersIcon,
  Pencil,
  Trash2,
} from 'lucide-react';
import { usersApi, organizationsApi } from '../services/api';
import { User, Organization } from '../types';
import { useAuth } from '../auth/AuthContext';

interface AdminUsersProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

type AuthError = Error & { name?: string };

const AdminUsers: React.FC<AdminUsersProps> = ({ addNotification }) => {
  const { user: authUser, roles, loading: authLoading, signIn: authSignIn, signOut: authSignOut } = useAuth();
  const isAdmin = roles.some((r) => r.toUpperCase() === 'ADMIN');
  const [sessionUser, setSessionUser] = useState<string | null>(null);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [confirmationEmail, setConfirmationEmail] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [working, setWorking] = useState(false);

  // CRUD state
  const [users, setUsers] = useState<User[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formRole, setFormRole] = useState<'ADMIN' | 'MEMBER'>('MEMBER');
  const [formName, setFormName] = useState('');
  const [formStatus, setFormStatus] = useState<boolean>(true);
  const [formOrganizationId, setFormOrganizationId] = useState<number | null>(null);
  
  // Organizations state
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  const getSelectedOrgId = () => {
    const orgIdStr = localStorage.getItem('selectedOrganizationId');
    return orgIdStr ? parseInt(orgIdStr) : null;
  };

  useEffect(() => {
    if (authUser?.username) setSessionUser(authUser.username);
    else setSessionUser(null);
  }, [authUser?.username]);

  useEffect(() => {
    // Load organizations
    const loadOrganizations = async () => {
      setLoadingOrgs(true);
      try {
        const orgs = await organizationsApi.list();
        setOrganizations(orgs);
        // Set default to selected org (localStorage) or first org
        const currentOrgId = getSelectedOrgId() || (orgs.length > 0 ? orgs[0].id : null);
        setFormOrganizationId(currentOrgId);
      } catch (err: any) {
        console.error('Failed to load organizations', err);
        addNotification('Failed to load organizations', 'error');
      } finally {
        setLoadingOrgs(false);
      }
    };
    loadOrganizations();
  }, []);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      if (isAdmin) {
        // Admins see users across all organizations
        const orgs = organizations.length > 0 ? organizations : await organizationsApi.list();
        const allUsersNested = await Promise.all(
          orgs.map(async (org) => {
            try {
              const list = await usersApi.list(org.id);
              // Ensure organization_id present for downstream UI uses
              return list.map((u) => ({ ...u, organization_id: u.organization_id ?? org.id }));
            } catch (err) {
              console.error(`Failed to load users for org ${org.id}`, err);
              return [];
            }
          })
        );
        setUsers(allUsersNested.flat());
      } else {
        const orgId = getSelectedOrgId();
        if (!orgId) {
          setUsers([]);
          return;
        }
        const list = await usersApi.list(orgId);
        setUsers(list);
      }
    } catch (err: any) {
      console.error('Failed to load users', err);
      addNotification('Failed to load users', 'error');
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    // Wait for auth to be ready before loading users
    if (!authLoading) {
      loadUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAdmin, organizations]);

  const resetForm = () => {
    setFormEmail('');
    setFormPassword('');
    setFormRole('MEMBER');
    setFormName('');
    setFormStatus(true);
    setEditingUser(null);
    const defaultOrgId = getSelectedOrgId() || (organizations.length > 0 ? organizations[0].id : null);
    setFormOrganizationId(defaultOrgId);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (user: User) => {
    setEditingUser(user);
    setFormEmail(user.email);
    setFormName(user.name || '');
    setFormRole(user.role);
    setFormStatus(user.is_active);
    setFormPassword('');
    setShowForm(true);
  };

  const handleCreateUser = async () => {
    if (!formOrganizationId) {
      addNotification('Select an organization first', 'warning');
      return;
    }
    if (!formEmail || !formPassword) {
      addNotification('Email and password are required', 'warning');
      return;
    }
    if (formPassword.length < 6) {
      addNotification('Password must be at least 6 characters', 'warning');
      return;
    }
    try {
      setWorking(true);
      await usersApi.createWithFirebase(formOrganizationId, {
        email: formEmail,
        password: formPassword,
        name: formName || formEmail,
        role: formRole,
        organization_id: formOrganizationId,
      });
      addNotification('User created in Firebase and synced to database.', 'success');
      setShowForm(false);
      resetForm();
      const currentOrgId = getSelectedOrgId();
      if (currentOrgId && formOrganizationId === currentOrgId) {
        await loadUsers();
      }
    } catch (error: any) {
      const msg = error?.response?.data?.detail || error?.message || 'Failed to create user';
      addNotification(String(msg), 'error');
    } finally {
      setWorking(false);
    }
  };

  const handleUpdateUser = async () => {
    const currentOrgId = getSelectedOrgId();
    if (!editingUser || !currentOrgId) return;
    try {
      setWorking(true);
      await usersApi.update(currentOrgId, editingUser.id, {
        name: formName,
        is_active: formStatus,
      });
      if (editingUser.role !== formRole) {
        await usersApi.setRole(currentOrgId, editingUser.id, formRole);
      }
      addNotification('User updated', 'success');
      setShowForm(false);
      resetForm();
      await loadUsers();
    } catch (error: any) {
      addNotification(error?.message || 'Failed to update user', 'error');
    } finally {
      setWorking(false);
    }
  };

  const handleDeleteUser = async (user: User) => {
    const currentOrgId = getSelectedOrgId();
    if (!currentOrgId) return;
    if (!window.confirm(`Delete user ${user.email}? This cannot be undone.`)) return;
    try {
      setWorking(true);
      await usersApi.delete(currentOrgId, user.id);
      addNotification('User deleted', 'success');
      await loadUsers();
    } catch (error: any) {
      addNotification(error?.message || 'Failed to delete user', 'error');
    } finally {
      setWorking(false);
    }
  };

  const handleSetRole = async (user: User, role: 'ADMIN' | 'MEMBER') => {
    const currentOrgId = getSelectedOrgId();
    if (!currentOrgId) return;
    try {
      setWorking(true);
      await usersApi.setRole(currentOrgId, user.id, role);
      addNotification(`Role updated to ${role}`, 'success');
      await loadUsers();
    } catch (error: any) {
      addNotification(error?.message || 'Failed to update role', 'error');
    } finally {
      setWorking(false);
    }
  };

  const handleConfirmUser = async () => {
    addNotification('Firebase: email verification is optional. User can sign in once created.', 'info');
  };

  const handleResendCode = async () => {
    addNotification('Firebase: use "Forgot password" on the login page if needed.', 'info');
  };

  const handleTestSignInAsNewUser = async () => {
    if (!formEmail || !formPassword) {
      addNotification('Email and password required to test sign-in', 'warning');
      return;
    }
    try {
      setWorking(true);
      await authSignIn({ username: formEmail, password: formPassword });
      setSessionUser(formEmail);
      addNotification('Signed in as the newly created user.', 'success');
    } catch (error) {
      const err = error as AuthError;
      addNotification(err?.message || 'Failed to sign in as new user', 'error');
    } finally {
      setWorking(false);
    }
  };

  const handleAdminSignIn = async () => {
    if (!adminEmail || !adminPassword) {
      addNotification('Admin email and password required', 'warning');
      return;
    }
    try {
      setWorking(true);
      await authSignIn({ username: adminEmail, password: adminPassword });
      setSessionUser(adminEmail);
      addNotification('Signed in as admin', 'success');
    } catch (error) {
      const err = error as AuthError;
      addNotification(err?.message || 'Failed to sign in', 'error');
    } finally {
      setWorking(false);
    }
  };

  const handleAdminSignOut = async () => {
    try {
      setWorking(true);
      await authSignOut();
      setSessionUser(null);
      addNotification('Signed out', 'info');
    } catch (error) {
      const err = error as AuthError;
      addNotification(err?.message || 'Failed to sign out', 'error');
    } finally {
      setWorking(false);
    }
  };

  const disabled = useMemo(() => working, [working]);
  const formIsCreate = !editingUser;

  return (
    <div className="space-y-6">
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6 flex items-start justify-between">
        <div>
          <div className="flex items-center space-x-2">
            <Shield className="h-6 w-6 text-indigo-600" />
            <h1 className="text-2xl font-bold text-slate-100">User Administration</h1>
          </div>
          <p className="text-slate-300 mt-2">
            Manage Cognito users, roles, and sync to the local database.
          </p>
        </div>
        {sessionUser ? (
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-700 text-sm">
            <CheckCircle className="h-4 w-4 mr-1" />
            Signed in as {sessionUser}
          </span>
        ) : (
          <span className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-100 text-yellow-700 text-sm">
            Not signed in
          </span>
        )}
      </div>

      {/* Users table */}
      <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <UsersIcon className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-slate-100">Users</h2>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={loadUsers}
              disabled={loadingUsers || working}
              className="inline-flex items-center px-3 py-2 rounded-md bg-slate-700 text-slate-200 hover:bg-slate-600 disabled:opacity-60"
            >
              <RefreshCcw className="h-4 w-4 mr-2" />
              Refresh
            </button>
            <button
              onClick={openCreate}
              className="inline-flex items-center px-3 py-2 rounded-md bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              New user
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-400 uppercase">Email</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-400 uppercase">Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-400 uppercase">Role</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-slate-400 uppercase">Status</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-slate-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-slate-800 divide-y divide-gray-200">
              {loadingUsers ? (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500">
                    Loading users...
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500">
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id}>
                    <td className="px-4 py-2 text-sm text-slate-100">{u.email}</td>
                    <td className="px-4 py-2 text-sm text-slate-200">{u.name || '-'}</td>
                    <td className="px-4 py-2 text-sm">
                      <select
                        value={u.role}
                        onChange={(e) => handleSetRole(u, e.target.value as 'ADMIN' | 'MEMBER')}
                        className="rounded-md border border-slate-600 px-2 py-1 text-sm bg-slate-800"
                        disabled={working}
                      >
                        <option value="MEMBER">Member</option>
                        <option value="ADMIN">Admin</option>
                      </select>
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          u.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-700 text-slate-300'
                        }`}
                      >
                        {u.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right space-x-2">
                      <button
                        onClick={() => openEdit(u)}
                        className="inline-flex items-center px-2 py-1 rounded-md bg-slate-700 text-slate-200 hover:bg-slate-600 text-sm"
                      >
                        <Pencil className="h-4 w-4 mr-1" /> Edit
                      </button>
                      <button
                        onClick={() => handleDeleteUser(u)}
                        className="inline-flex items-center px-2 py-1 rounded-md bg-red-100 text-red-700 hover:bg-red-200 text-sm"
                        disabled={working}
                      >
                        <Trash2 className="h-4 w-4 mr-1" /> Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6 space-y-4">
          <div className="flex items-center space-x-2">
            <UserPlus className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-slate-100">{formIsCreate ? 'Create user' : 'Edit user'}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-200">Email</label>
              <input
                type="email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-600 px-3 py-2 focus:border-purple-500 focus:outline-none"
                placeholder="user@example.com"
                disabled={!formIsCreate}
              />
            </div>
            {formIsCreate && (
              <div>
                <label className="block text-sm font-medium text-slate-200">Organization *</label>
                <select
                  value={formOrganizationId || ''}
                  onChange={(e) => setFormOrganizationId(parseInt(e.target.value))}
                  className="mt-1 w-full rounded-md border border-slate-600 px-3 py-2 focus:border-purple-500 focus:outline-none bg-slate-800"
                  disabled={loadingOrgs}
                >
                  <option value="">Select organization...</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-slate-200">Role</label>
              <select
                value={formRole}
                onChange={(e) => setFormRole(e.target.value as 'ADMIN' | 'MEMBER')}
                className="mt-1 w-full rounded-md border border-slate-600 px-3 py-2 focus:border-purple-500 focus:outline-none bg-slate-800"
              >
                <option value="MEMBER">Member</option>
                <option value="ADMIN">Admin</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-200">Name (optional)</label>
              <input
                type="text"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-600 px-3 py-2 focus:border-purple-500 focus:outline-none"
                placeholder="Full name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-200">Status</label>
              <select
                value={formStatus ? 'active' : 'inactive'}
                onChange={(e) => setFormStatus(e.target.value === 'active')}
                className="mt-1 w-full rounded-md border border-slate-600 px-3 py-2 focus:border-purple-500 focus:outline-none bg-slate-800"
                disabled={formIsCreate} // new users start active
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            {formIsCreate && (
              <div>
                <label className="block text-sm font-medium text-slate-200">Temporary password</label>
                <input
                  type="password"
                  value={formPassword}
                  onChange={(e) => setFormPassword(e.target.value)}
                  className="mt-1 w-full rounded-md border border-slate-600 px-3 py-2 focus:border-purple-500 focus:outline-none"
                  placeholder="Use a strong starter password"
                />
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={formIsCreate ? handleCreateUser : handleUpdateUser}
              disabled={disabled}
              className="inline-flex items-center px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60"
            >
              {formIsCreate ? 'Create user' : 'Save changes'}
            </button>
            <button
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
              className="inline-flex items-center px-4 py-2 rounded-md bg-slate-700 text-slate-200 hover:bg-slate-600 text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Admin session and confirmation utilities */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Admin session */}
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6 space-y-4">
          <div className="flex items-center space-x-2">
            <LogIn className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-slate-100">Admin session</h2>
          </div>
          <p className="text-sm text-slate-300">
            Sign in with an admin-capable Firebase user to perform management tasks.
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-200">Admin email</label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-600 px-3 py-2 focus:border-purple-500 focus:outline-none"
                placeholder="admin@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-200">Password</label>
              <input
                type="password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-600 px-3 py-2 focus:border-purple-500 focus:outline-none"
                placeholder="••••••••"
              />
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleAdminSignIn}
                disabled={disabled}
                className="flex-1 inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Sign in
              </button>
              <button
                onClick={handleAdminSignOut}
                disabled={disabled}
                className="inline-flex items-center justify-center rounded-md bg-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-600 disabled:opacity-60"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign out
              </button>
            </div>
          </div>
        </div>

        {/* Confirm and test */}
        <div className="bg-slate-800 rounded-lg shadow-sm border border-slate-700 p-6 space-y-4">
          <div className="flex items-center space-x-2">
            <Mail className="h-5 w-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-slate-100">Confirm & test login</h2>
          </div>
          <p className="text-sm text-slate-300">
            Use the emailed code to confirm, optionally resend, and test sign-in.
          </p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-200">Email/Username</label>
              <input
                type="email"
                value={confirmationEmail}
                onChange={(e) => setConfirmationEmail(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-600 px-3 py-2 focus:border-purple-500 focus:outline-none"
                placeholder="user@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-200">Confirmation code</label>
              <input
                type="text"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-600 px-3 py-2 focus:border-purple-500 focus:outline-none"
                placeholder="123456"
              />
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleConfirmUser}
                disabled={disabled}
                className="flex-1 inline-flex items-center justify-center rounded-md bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-60"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Confirm user
              </button>
              <button
                onClick={handleResendCode}
                disabled={disabled}
                className="inline-flex items-center justify-center rounded-md bg-slate-700 px-4 py-2 text-sm font-semibold text-slate-200 hover:bg-slate-600 disabled:opacity-60"
              >
                <RefreshCcw className="h-4 w-4 mr-2" />
                Resend code
              </button>
            </div>
            <div className="border-t pt-3 space-y-2">
              <button
                onClick={handleTestSignInAsNewUser}
                disabled={disabled}
                className="inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 w-full"
              >
                <LogIn className="h-4 w-4 mr-2" />
                Test login as new user
              </button>
              <p className="text-xs text-gray-500">
                Tip: after testing, use “Sign out” in Admin session to return to your admin account.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUsers;

